import React from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  WifiOff as WifiOffIcon
} from '@mui/icons-material';

const FirstTimeConnectionRequired = ({ connectionStatus, onRetry }) => {
  // Ensure connectionStatus is a valid object
  if (!connectionStatus || typeof connectionStatus !== 'object' || connectionStatus.message) {
    connectionStatus = {
      isReconnecting: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 2
    };
  }

  const isReconnecting = connectionStatus?.isReconnecting || false;
  const reconnectAttempts = connectionStatus?.reconnectAttempts || 0;
  const maxReconnectAttempts = connectionStatus?.maxReconnectAttempts || 2;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: (theme) => theme.palette.background.default,
        padding: 3,
        gap: 3
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          backgroundColor: (theme) => theme.palette.background.paper
        }}
      >
        {/* Icon */}
        <Box sx={{ mb: 3 }}>
          {isReconnecting ? (
            <CircularProgress size={64} sx={{ color: 'primary.main' }} />
          ) : (
            <WifiOffIcon sx={{ fontSize: 64, color: 'grey.400' }} />
          )}
        </Box>

        {/* Title */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
          {isReconnecting ? 'Connecting...' : 'Connection Required'}
        </Typography>

        {/* Description */}
        <Typography 
          variant="body1" 
          color="text.secondary" 
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          {isReconnecting 
            ? `Establishing connection to the server...`
            : `Please ensure you have an active internet connection to access your inbox for the first time.`
          }
        </Typography>

        {/* Reconnection Progress */}
        {isReconnecting && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'medium' }}>
              Attempt {reconnectAttempts} of {maxReconnectAttempts}
            </Typography>
            <Box sx={{ 
              width: '100%', 
              height: 4, 
              backgroundColor: 'grey.200', 
              borderRadius: 2, 
              mt: 1,
              overflow: 'hidden'
            }}>
              <Box sx={{ 
                width: `${(reconnectAttempts / maxReconnectAttempts) * 100}%`, 
                height: '100%', 
                backgroundColor: 'primary.main',
                transition: 'width 0.3s ease'
              }} />
            </Box>
          </Box>
        )}

        {/* Action Button */}
        <Button
          variant="contained"
          size="large"
          onClick={onRetry}
          disabled={isReconnecting}
          startIcon={isReconnecting ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'medium',
            minWidth: 200
          }}
        >
          {isReconnecting ? 'Connecting...' : 'Retry Connection'}
        </Button>

        {/* Additional Info */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          This is required only for the first-time setup. Once connected, you can access your inbox even with limited connectivity.
        </Typography>
      </Paper>
    </Box>
  );
};

export default FirstTimeConnectionRequired;
